import * as PIXI from "pixi.js";
import { ScrollableTimeline } from "../Shared/ScrollableTimeline.js";
import { TimelineMode, MouseClickType, NoteLength } from "../Settings/Enums.js";
import { NoteTimelineEvent } from "../Shared/TrackTimelineEvent.js";
import NoteHelper from "../../HelperModules/NoteHelper.js";
import { UIColors, UIPositioning } from "../Settings/UITheme.js";
import { NoteGroupMarker } from "./NoteGroupMarker.js";
import { ObjectPool } from "../../HelperModules/ObjectPool.js";
import { editType } from "../Settings/EditType.js";
import { mouseState } from "../Shared/MouseState.js";
export class SequencerTimeline extends ScrollableTimeline {
    get noteLength() {
        return editType.noteLength;
    }
    constructor(startX, endX, endY, contentHeight, songManager, track, startFrom) {
        super(startX, endX, 0, endY, songManager);
        this.timelineMode = TimelineMode.Edit;
        this.track = track;
        this._contentHeight = contentHeight;
        this._initialiseNoteGroups = this._initialiseNoteGroups.bind(this);
        this.track.noteGroupsChanged.addListener(this._initialiseNoteGroups);
        this._noteGroupContainer = new PIXI.Container();
        this._noteGroupPool = new ObjectPool();
        this._newEventGraphics = new PIXI.Graphics();
        this.addChildAt(this._newEventGraphics, this.children.length - 3);
        this.addChildAt(this._noteGroupContainer, this.children.length - 1);
        this._regenerateTimeline(startFrom);
    }
    get contentHeight() {
        return this._contentHeight;
    }
    get offsetContentHeight() {
        return this.contentHeight - SequencerTimeline.noteHeight;
    }
    pointerMoveHandler(event) {
        this._newEventGraphics.visible = false;
        super.pointerMoveHandler(event);
        if (this.timelineMode == TimelineMode.Edit && mouseState.buttons === 0) {
            this._newEventData = undefined;
            let mousePos = event.data.getLocalPosition(this.parent);
            if (mousePos.x < this.startX || mousePos.x > this.endX || mousePos.y < (this.startY + UIPositioning.timelineHeaderHeight) || mousePos.y > this.endY) {
                return;
            }
            let y = mousePos.y - this._verticalScrollPosition;
            y = y - (y % 20);
            let noteString = NoteHelper.noteNumberToNoteString(Math.max(0, (this.offsetContentHeight - y) / SequencerTimeline.noteHeight));
            let [barPosition, beatPosition, numBeats] = this._getBarFromStageCoordinates(mousePos.x);
            beatPosition = this.snapBeatToDragType(beatPosition);
            barPosition += beatPosition / numBeats;
            let startPosition = this.metadata.positionBarsToQuarterNote(barPosition);
            let length = SequencerTimeline.noteLengthDict[this.noteLength];
            let noteData = { pitchString: noteString, startPosition: startPosition, duration: length };
            let events = this.track.track.timeline.getEventsBetweenTimes(startPosition, startPosition + length);
            for (let i = 0; i < events.length; i++) {
                if (events[i].pitchString === noteData.pitchString) {
                    return;
                }
            }
            let x = this._getStageCoordinatesFromBar(barPosition);
            y += this._verticalScrollPosition;
            let width = (this.metadata.positionQuarterNoteToBeats(startPosition + length) - this.metadata.positionQuarterNoteToBeats(startPosition)) * this.beatWidth;
            this._newEventGraphics.clear()
                .beginFill(UIColors.trackEventColor)
                .drawRect(x, y + 1, width, SequencerTimeline.noteHeight - 1)
                .endFill()
                .beginHole()
                .drawRect(x + 2, y + 3, width - 4, SequencerTimeline.noteHeight - 5)
                .endHole();
            this._newEventData = noteData;
            this._newEventGraphics.visible = true;
        }
    }
    pointerUpClickHandler(event) {
        super.pointerUpClickHandler(event);
        if (this._mouseClickType == MouseClickType.LeftClick && this.timelineMode == TimelineMode.Edit && this._newEventData != undefined) {
            let noteEvent = this.track.addEvent(this._newEventData.startPosition, this._newEventData.pitchString, this._newEventData.duration);
            this._initialiseNoteGroups();
            this._initialiseNote(noteEvent);
            this._newEventData = undefined;
        }
    }
    mouseWheelHandler(event, canvasX, canvasY) {
        this._newEventGraphics.visible = false;
        super.mouseWheelHandler(event, canvasX, canvasY);
    }
    addedHandler() {
        super.addedHandler();
        editType.noteLengthDisabled = false;
    }
    destroy() {
        this.track.noteGroupsChanged.removeListener(this._initialiseNoteGroups);
        super.destroy();
    }
    _regenerateTimeline(fromBar, toBar) {
        super._regenerateTimeline(fromBar, toBar);
        this._initialiseNoteGroups();
    }
    _offsetChildren(pixelOffset) {
        super._offsetChildren(pixelOffset);
        this._noteGroupContainer.children.forEach(child => {
            child.x -= pixelOffset;
        });
    }
    _initialiseTrackTimelineEvents() {
        let notes = this.track.track.timeline.events;
        notes.forEach(note => {
            this._initialiseNote(note);
        });
    }
    _initialiseNote(note) {
        let y = this.offsetContentHeight - NoteHelper.noteStringToNoteNumber(note.pitchString) * SequencerTimeline.noteHeight;
        let timelineEvent = new NoteTimelineEvent(this, this.track, note, y, SequencerTimeline.noteHeight - 1);
        timelineEvent.borderHeight = 1;
        this._eventContainer.addChild(timelineEvent);
        return timelineEvent;
    }
    _initialiseNoteGroups() {
        this._noteGroupContainer.children.forEach(noteGroupMarker => {
            noteGroupMarker.visible = false;
            this._noteGroupPool.returnObject(noteGroupMarker);
        });
        this.track.noteGroups.forEach(noteGroup => {
            let marker = this._noteGroupPool.getObject();
            if (marker == null) {
                marker = new NoteGroupMarker(this);
                this._noteGroupContainer.addChild(marker);
            }
            marker.visible = true;
            marker.reinitialise(noteGroup);
        });
    }
}
SequencerTimeline.noteHeight = 20;
SequencerTimeline.noteLengthDict = {
    [NoteLength.Whole]: 4,
    [NoteLength.Half]: 2,
    [NoteLength.Quarter]: 1,
    [NoteLength.Eighth]: 0.5,
    [NoteLength.Sixteenth]: 0.25,
    [NoteLength.ThirtySecond]: 0.125
};
//# sourceMappingURL=SequencerTimeline.js.map